import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutDashboardIcon, ListIcon, LineChartIcon, SlidersHorizontalIcon } from 'lucide-react';
import { usePermissions } from '../../custom-hooks/use-permissions';
import DashboardView from './DashboardView';
import ItemsView from './ItemsView';
import ReportsView from './ReportsView';
import ManageView from './ManageView';

const TABS = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboardIcon, permission: 'view_dashboard' },
    { key: 'items', label: 'Items', icon: ListIcon, permission: 'view_items' },
    { key: 'reports', label: 'Reports', icon: LineChartIcon, permission: 'view_reports' },
    { key: 'manage', label: 'Manage', icon: SlidersHorizontalIcon, permission: 'manage_tab' },
];

// A single indicator pill that slides between tab buttons (measured via refs)
// instead of each button independently toggling its own background on/off.
const TabBar = ({ tabs, activeTab, onChange }) => {
    const tabRefs = useRef({});
    const [indicator, setIndicator] = useState(null);

    useLayoutEffect(() => {
        const el = tabRefs.current[activeTab];
        if (el) {
            setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
        }
    }, [activeTab, tabs.length]);

    useEffect(() => {
        const handleResize = () => {
            const el = tabRefs.current[activeTab];
            if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeTab]);

    return (
        <div className="relative mb-6 flex gap-1 overflow-x-auto rounded-2xl border border-inv-cream-200 bg-white p-1.5 shadow-sm">
            {indicator && (
                <div
                    className="absolute top-1.5 bottom-1.5 rounded-xl bg-inv-brand shadow-[0_8px_18px_-10px_rgba(194,87,26,0.7)] transition-all duration-300 ease-out"
                    style={{ left: indicator.left, width: indicator.width }}
                />
            )}
            {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.key;
                return (
                    <button
                        key={tab.key}
                        ref={(el) => { tabRefs.current[tab.key] = el; }}
                        onClick={() => onChange(tab.key)}
                        className={`relative z-10 flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                            active ? 'text-white' : 'text-[#6B675F] hover:text-inv-ink'
                        }`}
                    >
                        <Icon size={16} />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
};

const InventoryModule = () => {
    const { can } = usePermissions();
    const visibleTabs = TABS.filter((tab) => can('FX31', tab.permission));

    // Tab lives in the URL (not just local state) so a link into the module — e.g. the
    // item detail page's "Back to Inventory" — can land on a specific tab instead of
    // always resetting on remount. Falls back to the first tab this user can actually
    // see (not a hardcoded "dashboard") since permissions now vary per tab.
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');
    const activeTab = visibleTabs.some((t) => t.key === requestedTab) ? requestedTab : visibleTabs[0]?.key;
    const setActiveTab = (tab) => setSearchParams({ tab });

    return (
        <div className="min-h-screen bg-inv-cream animate-invFadeIn">
            <div className="px-4 py-6 md:px-8 md:py-8">
                <div className="mb-6">
                    <h1 className="text-2xl font-extrabold tracking-tight text-inv-ink md:text-[26px]">Inventory</h1>
                    <p className="mt-1 text-sm text-[#8C8175]">Track daily consumption, stock levels, and alerts for office consumables.</p>
                </div>

                {visibleTabs.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-inv-cream-200 bg-white py-16 text-center text-sm text-[#8C8175]">
                        You don't have access to any section of Inventory yet — ask an admin to grant permissions in Settings &gt; Roles.
                    </div>
                ) : (
                    <>
                        <TabBar tabs={visibleTabs} activeTab={activeTab} onChange={setActiveTab} />
                        <div key={activeTab} className="animate-invPopIn">
                            {activeTab === 'dashboard' && <DashboardView onNavigate={setActiveTab} />}
                            {activeTab === 'items' && <ItemsView />}
                            {activeTab === 'reports' && <ReportsView />}
                            {activeTab === 'manage' && <ManageView />}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default InventoryModule;
